import { format } from "date-fns";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChangelogEntry as ChangelogEntryType } from "@/lib/api/changelog";
import { LoomEmbed } from "./loom-embed";

interface ChangelogEntryProps {
  entry: ChangelogEntryType;
}

function deriveDateLabel(entry: ChangelogEntryType): string {
  if (entry.date_label) return entry.date_label;
  const iso = entry.published_at ?? entry.created_at;
  try {
    return format(new Date(iso), "MMM yyyy");
  } catch {
    return "";
  }
}

export function ChangelogEntry({ entry }: ChangelogEntryProps) {
  const hasLoom = !!entry.loom_url;
  const hasChanges = entry.changes.length > 0;
  const dateLabel = deriveDateLabel(entry);

  return (
    <div className="flex items-start gap-6">
      <div className="flex shrink-0 flex-col items-center self-stretch pt-1.5">
        <div className="size-2 rounded-full bg-[#dfdad3]" />
        <div className="min-h-0 w-0.5 flex-1 rounded-full bg-[#dfdad3]" />
      </div>

      <div className="grid flex-1 grid-cols-1 gap-6 md:grid-cols-2">
        <div className="flex flex-col gap-3">
          {dateLabel && (
            <p className="text-sm text-muted-foreground">{dateLabel}</p>
          )}
          <h3 className="text-base font-semibold text-foreground">
            {entry.title}
          </h3>
          {entry.subtitle && (
            <p className="text-sm text-muted-foreground">{entry.subtitle}</p>
          )}
          <div className="prose prose-sm max-w-none text-sm text-muted-foreground prose-headings:text-foreground prose-strong:text-foreground prose-a:text-primary prose-li:my-1 prose-ul:my-2">
            <ReactMarkdown remarkPlugins={[remarkGfm]}>
              {entry.body}
            </ReactMarkdown>
          </div>
          {hasChanges && (
            <div className="text-sm text-muted-foreground">
              <p className="mb-1">Changes:</p>
              <ul className="ml-5 list-disc space-y-1">
                {entry.changes.map((change, i) => (
                  <li key={i}>{change}</li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {hasLoom && entry.loom_url && (
          <div className="flex items-center">
            <LoomEmbed url={entry.loom_url} />
          </div>
        )}
      </div>
    </div>
  );
}
